import { telemetryService } from './telemetryService';

/**
 * Funnel step tracker — tracks sequential steps in user journeys.
 * Each step is a custom event with a funnelName + stepName + stepIndex.
 * Application Insights can then build funnel visualizations from these.
 */

export const FunnelNames = {
  ONBOARDING: 'funnel.onboarding',
  WORKFLOW_DESIGN: 'funnel.workflow_design',
  WORKFLOW_EXECUTION: 'funnel.workflow_execution',
  AGENT_CHAT: 'funnel.agent_chat',
  CONNECTION_SETUP: 'funnel.connection_setup',
} as const;

type FunnelName = (typeof FunnelNames)[keyof typeof FunnelNames];

/**
 * Funnel step definitions — documents the expected step sequence for each funnel.
 * Array index is the stepIndex. Use these as reference when calling trackFunnelStep.
 */
export const FunnelSteps: Record<FunnelName, readonly string[]> = {
  [FunnelNames.ONBOARDING]: [
    'login',
    'select_subscription',
    'select_project',
    'select_app',
    'create_workflow',
    'add_first_node',
    'publish',
  ],
  [FunnelNames.WORKFLOW_DESIGN]: [
    'open_workflow',
    'add_node',
    'configure_node',
    'save_draft',
    'publish',
  ],
  [FunnelNames.WORKFLOW_EXECUTION]: [
    'trigger_workflow',
    'view_run',
    'inspect_action',
    'debug_issue',
  ],
  [FunnelNames.AGENT_CHAT]: [
    'open_chat',
    'send_message',
    'review_proposal',
    'accept_proposal',
    'publish',
  ],
  [FunnelNames.CONNECTION_SETUP]: [
    'open_picker',
    'select_connector',
    'start_oauth',
    'complete_auth',
    'test_connection',
    'use_in_node',
  ],
} as const;

/** Track a funnel step. Call in sequence as user progresses. */
export function trackFunnelStep(
  funnelName: FunnelName,
  stepName: string,
  stepIndex: number,
  properties?: Record<string, unknown>
): void {
  const { funnelName: _f, stepName: _s, stepIndex: _i, ...safeProperties } = properties ?? {};
  telemetryService.trackEvent(
    'funnel.step',
    { ...safeProperties, funnelName, stepName },
    { stepIndex }
  );
}
